import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import type { InvoiceStatus } from "@rentorng/core";
import { api } from "@/lib/api";
import { titleCase, useFormat } from "@/lib/format";
import { useApiMutation } from "@/lib/mutations";
import { useSession } from "@/lib/session";
import { InvoiceStatusBadge } from "@/components/status";
import { Alert, Button, Card, CardTitle, EmptyState, ErrorState, Field, LinkButton, PageHeader, Select, Spinner, Table } from "@/components/ui";

/**
 * Sending reminders by hand.
 *
 * Manual sending is on every plan, including the free one, because a manager has to feel the
 * engine work before paying for it. The preview is the important part of this screen: it shows
 * the resolved text for a real recipient, so nobody sends four hundred emails containing
 * `{{tenant.frist_name}}`.
 */

interface InvoiceRow {
  id: string;
  number: string;
  dueDate: string;
  total: number;
  paid: number;
  status: string;
  partyId: string;
  partyName: string;
  partyEmail: string | null;
  unitLabel: string | null;
  propertyName: string | null;
}

export function ReminderSendPage() {
  const [search] = useSearchParams();
  const { vocab } = useSession();
  const fmt = useFormat();

  const scope = search.get("scope");
  const presetInvoiceIds = (search.get("invoiceIds") ?? "").split(",").filter(Boolean);
  const presetPartyId = search.get("partyId");
  const presetLeaseId = search.get("leaseId");

  const [selected, setSelected] = useState<string[]>(presetInvoiceIds);
  const [templateId, setTemplateId] = useState("");

  const templates = useQuery({
    queryKey: ["templates"],
    queryFn: () => api.get<{ templates: Array<{ id: string; name: string; trigger: string | null; channel: string }> }>("/templates"),
  });

  const invoices = useQuery({
    queryKey: ["invoices", "reminder", presetPartyId, scope],
    queryFn: () =>
      api.get<{ invoices: InvoiceRow[]; today: string; graceDays: number }>("/invoices", {
        bucket: scope === "expiring" ? undefined : "overdue",
        partyId: presetPartyId ?? undefined,
      }),
    enabled: !presetLeaseId,
  });

  const expiring = useQuery({
    queryKey: ["leases", "expiring-soon"],
    queryFn: () => api.get<{ leases: Array<{ id: string; tenantName: string; unitLabel: string; endDate: string | null }> }>("/leases", { expiring: "d0_30" }),
    enabled: scope === "expiring",
  });

  useEffect(() => {
    if (!templateId && templates.data?.templates.length) {
      const preferred = templates.data.templates.find((template) =>
        scope === "expiring" ? template.trigger === "lease_expiring" : template.trigger === "rent_overdue",
      );
      setTemplateId(preferred?.id ?? templates.data.templates[0].id);
    }
  }, [templates.data, templateId, scope]);

  const previewQuery = useQuery({
    queryKey: ["reminder-resolve", templateId, selected.slice(0, 3).join(",")],
    queryFn: () =>
      api.post<{ previews: Array<{ to: string; name?: string; subject: string | null; body: string }> }>("/reminders/resolve", {
        templateId,
        invoiceIds: selected.slice(0, 3),
      }),
    enabled: Boolean(templateId),
  });

  const send = useApiMutation({
    mutationFn: () =>
      api.post<{ queued: number; skipped: number; reasons: string[]; delivery: { sent: number; simulated: number; failed: number } }>(
        "/reminders/send",
        {
          templateId,
          channel: "email",
          invoiceIds: scope === "expiring" ? undefined : selected,
          leaseIds: scope === "expiring" ? (expiring.data?.leases ?? []).map((lease) => lease.id) : presetLeaseId ? [presetLeaseId] : undefined,
          partyIds: presetPartyId && selected.length === 0 && !presetLeaseId ? [presetPartyId] : undefined,
          sendNow: true,
        },
      ),
    invalidates: [["messages"], ["reminder-preview"], ["dashboard"]],
  });

  const rows = invoices.data?.invoices ?? [];
  const toggle = (id: string) =>
    setSelected((current) => (current.includes(id) ? current.filter((value) => value !== id) : [...current, id]));

  const recipientCount =
    scope === "expiring" ? (expiring.data?.leases.length ?? 0) : presetLeaseId ? 1 : selected.length || (presetPartyId ? 1 : 0);

  return (
    <div>
      <PageHeader
        title="Send a reminder"
        back={{ to: "/app/reminders", label: "Reminders" }}
        subtitle="Pick who, pick the wording, read it back, then send. Nobody is sent the same reminder twice in a day."
      />

      {send.isSuccess && send.data ? (
        <div className="mb-5">
          <Alert tone={send.data.queued > 0 ? "success" : "warn"} title={`${send.data.queued} queued, ${send.data.skipped} skipped.`}>
            {send.data.delivery.simulated > 0
              ? `${send.data.delivery.simulated} were simulated because no mail server is configured — they are in the log with their full text.`
              : `${send.data.delivery.sent} sent, ${send.data.delivery.failed} failed.`}
            {send.data.reasons.length ? <span className="mt-1 block text-xs">{send.data.reasons.slice(0, 5).join(" · ")}</span> : null}
            <span className="mt-1 block">
              <Link to="/app/reminders" className="font-medium underline">
                See the log
              </Link>
            </span>
          </Alert>
        </div>
      ) : null}

      {send.errorMessage ? (
        <div className="mb-5">
          <Alert tone="danger">{send.errorMessage}</Alert>
        </div>
      ) : null}

      <div className="grid gap-5 lg:grid-cols-2">
        <Card padded={false}>
          <div className="p-4 sm:p-5">
            <CardTitle
              hint={
                scope === "expiring"
                  ? `Every ${vocab.lease} expiring within 30 days.`
                  : presetLeaseId
                    ? "This tenancy."
                    : `Overdue ${vocab.invoices}. Tick the ones to chase.`
              }
              action={
                rows.length > 0 && !presetLeaseId && scope !== "expiring" ? (
                  <button type="button" className="link" onClick={() => setSelected(selected.length === rows.length ? [] : rows.map((row) => row.id))}>
                    {selected.length === rows.length ? "Clear all" : "Select all"}
                  </button>
                ) : null
              }
            >
              Who gets it
            </CardTitle>
          </div>

          {scope === "expiring" ? (
            expiring.isLoading ? (
              <Spinner />
            ) : (
              <Table head={<tr><th>{titleCase(vocab.tenant)}</th><th>{titleCase(vocab.unit)}</th><th>Ends</th></tr>}>
                {(expiring.data?.leases ?? []).map((lease) => (
                  <tr key={lease.id}>
                    <td>{lease.tenantName}</td>
                    <td className="text-ink-500">{lease.unitLabel}</td>
                    <td className="text-ink-500">{fmt.date(lease.endDate)}</td>
                  </tr>
                ))}
              </Table>
            )
          ) : presetLeaseId ? (
            <p className="p-4 text-sm text-ink-500 sm:p-5">Sending about one {vocab.lease}.</p>
          ) : invoices.isLoading ? (
            <Spinner />
          ) : invoices.error ? (
            <ErrorState error={invoices.error} retry={() => invoices.refetch()} />
          ) : rows.length === 0 ? (
            <EmptyState title="Nothing is overdue." hint="That is the product working." />
          ) : (
            <Table
              head={
                <tr>
                  <th className="w-8"></th>
                  <th>Client</th>
                  <th>{titleCase(vocab.invoice)}</th>
                  <th>Status</th>
                  <th className="num">Balance</th>
                </tr>
              }
            >
              {rows.map((row) => (
                <tr key={row.id}>
                  <td>
                    <input
                      type="checkbox"
                      aria-label={`Select ${row.number}`}
                      className="size-4 rounded border-ink-300"
                      checked={selected.includes(row.id)}
                      onChange={() => toggle(row.id)}
                    />
                  </td>
                  <td>
                    {row.partyName}
                    {!row.partyEmail ? <span className="block text-xs text-danger-600">no email address</span> : null}
                  </td>
                  <td className="text-ink-500">
                    {row.number}
                    {row.unitLabel ? <span className="block text-xs text-ink-400">{row.unitLabel}</span> : null}
                  </td>
                  <td>
                    <InvoiceStatusBadge
                      invoice={{ dueDate: row.dueDate, total: row.total, paid: row.paid, status: row.status as InvoiceStatus }}
                      asOf={invoices.data?.today ?? ""}
                      graceDays={invoices.data?.graceDays ?? 0}
                    />
                  </td>
                  <td className="num tabular">{fmt.money(row.total - row.paid)}</td>
                </tr>
              ))}
            </Table>
          )}
        </Card>

        <div className="space-y-5">
          <Card>
            <CardTitle hint="Edit the wording under Templates.">The message</CardTitle>
            <Field label="Template" htmlFor="templateId">
              <Select id="templateId" value={templateId} onChange={(event) => setTemplateId(event.target.value)}>
                {(templates.data?.templates ?? []).map((template) => (
                  <option key={template.id} value={template.id}>
                    {template.name} ({template.channel})
                  </option>
                ))}
              </Select>
            </Field>

            <div className="mt-4">
              <p className="label">Preview</p>
              {previewQuery.isLoading ? (
                <Spinner label="Resolving…" />
              ) : previewQuery.data?.previews.length ? (
                <div className="space-y-3">
                  {previewQuery.data.previews.map((preview, index) => (
                    <div key={index} className="rounded-lg border border-ink-200 bg-ink-50 p-3">
                      <p className="text-xs text-ink-500">To: {preview.to}</p>
                      {preview.subject ? <p className="mt-1 text-sm font-medium">{preview.subject}</p> : null}
                      <p className="mt-1 whitespace-pre-wrap text-xs text-ink-700">{preview.body}</p>
                    </div>
                  ))}
                  {selected.length > 3 ? (
                    <p className="text-xs text-ink-500">Showing the first 3 of {selected.length} recipients.</p>
                  ) : null}
                </div>
              ) : (
                <p className="text-sm text-ink-500">Choose a template to preview it.</p>
              )}
            </div>
          </Card>

          <Card>
            <div className="flex flex-wrap items-center gap-2">
              <Button disabled={!templateId || recipientCount === 0 || send.isPending} onClick={() => send.mutate(undefined as never)}>
                {send.isPending ? "Sending…" : `Send to ${recipientCount} ${recipientCount === 1 ? "recipient" : "recipients"}`}
              </Button>
              <LinkButton to="/app/reminders" variant="secondary">
                Cancel
              </LinkButton>
            </div>
            <p className="mt-2 text-xs text-ink-500">
              Anyone without the right contact details is skipped and named in the result, rather than silently dropped.
            </p>
          </Card>
        </div>
      </div>
    </div>
  );
}
